import { Checkbox, Stack, Typography } from '@/components';
import Language from '@/entities/Language';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { LuStar } from 'react-icons/lu';
import styles from './LanguageMultiSelect.module.css';

interface LanguageOptionProps {
  option: Language;
  isSelected: boolean;
  toggle: () => void;
}

const LanguageOption = ({
  option,
  isSelected,
  toggle,
}: LanguageOptionProps) => {
  return (
    <div key={option.code} className={styles.option}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Checkbox checked={isSelected} onChange={toggle} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>
            {option.name} ({option.code})
          </Typography>
          {option.isMain ? (
            <LuStar className={styles.mainLanguageOptionIcon} />
          ) : null}
        </Stack>
      </Stack>
    </div>
  );
};

interface LanguageMultiSelectProps {
  options: Language[];
  initialOptions?: Language[];
  placeholder?: string;
  onChange: (selected: Language[]) => void;
  addLanguage: () => void;
  deleteLanguage: (language: Language) => void;
  setMainLanguage: (language: Language) => void;
}

const LanguageMultiSelect = ({
  options,
  initialOptions,
  placeholder = 'No languages',
  onChange,
}: LanguageMultiSelectProps): JSX.Element => {
  const [selectedOptions, setSelectedOptions] = useState<Language[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOptionToggle = (option: Language) => {
    const updatedOptions = selectedOptions.includes(option)
      ? selectedOptions.filter((selectedOption) => selectedOption !== option)
      : [...selectedOptions, option];

    setSelectedOptions(updatedOptions);

    if (onChange) {
      onChange(updatedOptions);
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const close =
      containerRef.current &&
      !containerRef.current.contains(event.target as Node);
    if (close) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick, true);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (initialOptions) {
      setSelectedOptions(initialOptions);
    }
  }, [initialOptions]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={classNames(styles.inputArea, { [styles.opened]: isOpen })}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        {selectedOptions.length > 0 ? (
          <Typography>
            {selectedOptions.length} language
            {selectedOptions.length > 1 ? 's' : ''}
          </Typography>
        ) : (
          <Typography className={styles.placeholder}>{placeholder}</Typography>
        )}
      </div>
      {isOpen && (
        <div className={styles.dropdown}>
          {options.map((option) => (
            <LanguageOption
              key={option.code}
              option={option}
              isSelected={selectedOptions.includes(option)}
              toggle={() => handleOptionToggle(option)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageMultiSelect;
